import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AppState } from '@core/constants/app-state.enum';
import { LoggedUser, SignInResult, isMfaChallenge } from '@core/models';
import { SelectOption } from '../../design-system/components/listbox/listbox.component';
import { getAngularService } from '../../ng-upgrade.utils';
import { AppService } from '../../services/app.service';
import { I18nService } from '../../services/i18n.service';
import { NavigationService } from '../../services/navigation.service';
import { NotificationService } from '../../services/notification.service';
import { SecurityService } from '../../services/security.service';
import { ToastService } from '../../services/toast.service';
import { UserService } from '../../services/user.service';
import {
  COUNTRY_OPTIONS,
  GENERIC_ERROR_MESSAGE_KEY,
  LEGACY_LOGIN_DATA_KEY,
  LOGIN_ERROR_MESSAGE_KEYS,
  LOGIN_LOGO_PATH,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  REMEMBERED_LOGIN_KEY,
  detectCountryCode,
  extractLegacyLogin,
  isAllowedRedirect,
  isSafeCountryUrl,
  resolveCountryUrl,
} from './login.constants';
import {
  PendingMfaChallenge,
  clearPendingChallenge,
  readPendingChallenge,
  savePendingChallenge,
} from './mfa-challenge.storage';
import { MFA_ERROR_MESSAGE_KEYS } from './mfa.constants';

interface LoginHttpError {
  data?: { error?: string };
}

interface LoginLabels {
  title: string;
  username: string;
  usernamePlaceholder: string;
  usernameError: string;
  password: string;
  passwordPlaceholder: string;
  passwordError: string;
  requiredField: string;
  rememberMe: string;
  signIn: string;
  signingIn: string;
  showPassword: string;
  hidePassword: string;
  country: string;
}

@Component({
  selector: 'app-login',
  host: { class: 'block' },
  template: `
    <div
      class="aav-scope flex min-h-screen w-full flex-col items-center justify-center bg-aav-surface-container-high px-[20px] py-[60px]"
    >
      <div
        class="w-full max-w-[380px] rounded-aav-sm bg-aav-surface px-7 py-8 shadow-aav-elevation-card"
      >
        <img [src]="logoPath" alt="Auto Avaliar" class="mx-auto h-auto w-[154px]" />

        <h1
          *ngIf="!mfaChallenge"
          class="mt-[20px] text-center text-aav-title-small-strong leading-4 text-aav-on-surface"
        >
          {{ labels.title }}
        </h1>

        <form
          *ngIf="showForm && !mfaChallenge"
          class="mt-6 flex flex-col gap-4"
          [formGroup]="loginForm"
          (ngSubmit)="onSubmit()"
          novalidate
        >
          <aav-text-field
            formControlName="userloginRaw"
            type="email"
            name="userlogin"
            autocomplete="username"
            [label]="labels.username"
            [placeholder]="labels.usernamePlaceholder"
            [invalid]="isInvalid('userloginRaw')"
            [errorMessage]="usernameErrorMessage"
          ></aav-text-field>

          <aav-text-field
            formControlName="secret"
            name="secret"
            autocomplete="current-password"
            [type]="showPassword ? 'text' : 'password'"
            [label]="labels.password"
            [placeholder]="labels.passwordPlaceholder"
            [invalid]="isInvalid('secret')"
            [errorMessage]="passwordErrorMessage"
            [trailingIcon]="showPassword ? 'visibility_off' : 'visibility'"
            [trailingIconLabel]="showPassword ? labels.hidePassword : labels.showPassword"
            (trailingIconClick)="togglePassword()"
          ></aav-text-field>

          <aav-checkbox
            class="mb-1"
            formControlName="rememberPassword"
            [label]="labels.rememberMe"
          ></aav-checkbox>

          <aav-button variant="filled" type="submit" [fullWidth]="true" [loading]="loading">
            {{ loading ? labels.signingIn : labels.signIn }}
          </aav-button>

          <aav-listbox
            class="self-center"
            shape="pill"
            placement="above"
            [options]="countryOptions"
            [ariaLabel]="labels.country"
            [value]="currentCountry"
            (valueChange)="onCountryChange($event)"
          >
            <ng-template let-option>
              <app-country-flag [code]="option?.value || ''"></app-country-flag>
            </ng-template>
          </aav-listbox>
        </form>

        <app-mfa-code
          *ngIf="mfaChallenge"
          class="mt-6"
          [requestId]="mfaChallenge.requestId"
          [email]="mfaChallenge.email"
          [initialAttempts]="mfaChallenge.attempts"
          [codeSentAt]="mfaChallenge.codeSentAt"
          [canResend]="canResendCode"
          (verified)="onMfaVerified($event)"
          (attemptsChange)="onMfaAttemptsChange($event)"
          (resend)="onMfaResend()"
          (back)="onMfaBack()"
        ></app-mfa-code>
      </div>
    </div>
  `,
})
export class LoginComponent implements OnInit, OnDestroy {
  readonly logoPath = LOGIN_LOGO_PATH;

  readonly loginForm = new FormGroup({
    userloginRaw: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    secret: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(PASSWORD_MIN_LENGTH),
        Validators.maxLength(PASSWORD_MAX_LENGTH),
      ],
    }),
    rememberPassword: new FormControl<boolean>(false, { nonNullable: true }),
  });

  showForm = false;
  loading = false;
  showPassword = false;
  mfaChallenge: PendingMfaChallenge | null = null;

  countryOptions: SelectOption[] = [];
  currentCountry = detectCountryCode(window.location.hostname);

  labels: LoginLabels = {
    title: '',
    username: '',
    usernamePlaceholder: '',
    usernameError: '',
    password: '',
    passwordPlaceholder: '',
    passwordError: '',
    requiredField: '',
    rememberMe: '',
    signIn: '',
    signingIn: '',
    showPassword: '',
    hidePassword: '',
    country: '',
  };

  private pendingSecret = '';

  private appService: AppService | null = null;
  private i18nService: I18nService | null = null;
  private navigationService: NavigationService | null = null;
  private notificationService: NotificationService | null = null;
  private securityService: SecurityService | null = null;
  private toastService: ToastService | null = null;
  private userService: UserService | null = null;

  ngOnInit(): void {
    this.appService = getAngularService<AppService>(AppService);
    this.i18nService = getAngularService<I18nService>(I18nService);
    this.navigationService = getAngularService<NavigationService>(NavigationService);
    this.notificationService = getAngularService<NotificationService>(NotificationService);
    this.securityService = getAngularService<SecurityService>(SecurityService);
    this.toastService = getAngularService<ToastService>(ToastService);
    this.userService = getAngularService<UserService>(UserService);

    this.labels = this.buildLabels();
    this.countryOptions = this.buildCountryOptions();
    this.appService?.setLoading(false);

    const rememberedLogin = this.migrateLegacyLoginData();

    if (this.securityService?.hasTokenUsers()) {
      clearPendingChallenge();
      this.navigationService?.goToState(AppState.PanelDashboard);
      return;
    }

    this.showForm = true;
    this.prefillRememberedLogin(rememberedLogin);
    this.hideChatWidget();

    this.mfaChallenge = readPendingChallenge();
  }

  get canResendCode(): boolean {
    return this.pendingSecret !== '';
  }

  isInvalid(controlName: 'userloginRaw' | 'secret'): boolean {
    const control = this.loginForm.controls[controlName];
    return control.touched && control.invalid;
  }

  get usernameErrorMessage(): string {
    return this.loginForm.controls.userloginRaw.hasError('required')
      ? this.labels.requiredField
      : this.labels.usernameError;
  }

  get passwordErrorMessage(): string {
    return this.loginForm.controls.secret.hasError('required')
      ? this.labels.requiredField
      : this.labels.passwordError;
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  onCountryChange(code: string): void {
    const country = COUNTRY_OPTIONS.find((option) => option.code === code);

    if (!country || country.code === this.currentCountry) {
      return;
    }

    const url = this.reachableCountryUrl(country.code);

    if (!url) {
      this.toastService?.info(this.i18nService?.instant('LOGIN.COUNTRY_UNAVAILABLE') ?? '');
      return;
    }

    this.currentCountry = country.code;

    if (new URL(url).hostname === window.location.hostname) {
      return;
    }

    this.navigationService?.redirectToExternal(url);
  }

  async onSubmit(): Promise<void> {
    if (this.loading) {
      return;
    }
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const { userloginRaw, secret } = this.loginForm.getRawValue();
    const userlogin = userloginRaw.toLowerCase();

    const response = await this.authenticate(userlogin, secret);
    if (!response) {
      return;
    }

    if (isMfaChallenge(response)) {
      this.pendingSecret = secret;
      this.startChallenge(response.requestId, userlogin);
      return;
    }

    this.completeLogin(response, userlogin);
  }

  onMfaVerified(session: LoggedUser): void {
    const email = this.mfaChallenge?.email ?? '';

    this.discardChallenge();
    this.completeLogin(session, email);
  }

  onMfaAttemptsChange(attempts: number): void {
    if (!this.mfaChallenge) {
      return;
    }

    this.mfaChallenge = { ...this.mfaChallenge, attempts };
    savePendingChallenge(this.mfaChallenge);
  }

  async onMfaResend(): Promise<void> {
    const challenge = this.mfaChallenge;
    if (!challenge) {
      return;
    }

    if (!this.pendingSecret) {
      this.toastService?.info(
        this.i18nService?.instant('LOGIN.MFA.TOAST.RESEND_NEEDS_PASSWORD') ?? ''
      );
      this.onMfaBack();
      return;
    }

    const response = await this.authenticate(challenge.email, this.pendingSecret);

    if (!response) {
      this.restartResendCooldown();
      return;
    }

    if (!isMfaChallenge(response)) {
      this.discardChallenge();
      this.completeLogin(response, challenge.email);
      return;
    }

    this.startChallenge(response.requestId, challenge.email);
    this.toastService?.success(this.i18nService?.instant('LOGIN.MFA.TOAST.CODE_RESENT') ?? '');
  }

  onMfaBack(): void {
    this.discardChallenge();
    this.loginForm.controls.secret.reset('');
  }

  ngOnDestroy(): void {
    this.clearChallenge();
  }

  private startChallenge(requestId: string, email: string): void {
    const now = Date.now();

    this.mfaChallenge = { requestId, email, attempts: 0, createdAt: now, codeSentAt: now };
    savePendingChallenge(this.mfaChallenge);
  }

  private restartResendCooldown(): void {
    if (!this.mfaChallenge) {
      return;
    }

    this.mfaChallenge = { ...this.mfaChallenge, codeSentAt: Date.now() };
    savePendingChallenge(this.mfaChallenge);
  }

  private discardChallenge(): void {
    clearPendingChallenge();
    this.clearChallenge();
  }

  private clearChallenge(): void {
    this.mfaChallenge = null;
    this.pendingSecret = '';
  }

  private async authenticate(userlogin: string, secret: string): Promise<SignInResult | null> {
    const userService = this.userService;
    if (!userService) {
      this.showError(GENERIC_ERROR_MESSAGE_KEY);
      return null;
    }

    this.loading = true;
    this.appService?.setLoading(true);

    try {
      return await userService.authenticate({ userlogin, secret });
    } catch (error) {
      this.handleLoginError(error);
      return null;
    } finally {
      this.loading = false;
      this.appService?.setLoading(false);
    }
  }

  private completeLogin(response: LoggedUser, normalizedLogin: string): void {
    this.securityService?.startLogin(response);
    this.appService?.setLoggedUser(response.user ?? null);
    this.persistRememberedLogin(normalizedLogin);
    this.sendDeviceToken();
    this.toastService?.success(this.i18nService?.instant('LOGIN.SUCCESS.SIGNED_IN') ?? '');
    this.redirectAfterLogin(response);
  }

  private redirectAfterLogin(response: LoggedUser): void {
    const redirect = this.navigationService?.getQueryParam('redirect') ?? null;

    if (redirect && isAllowedRedirect(redirect)) {
      this.navigationService?.redirectToExternal(redirect);
      return;
    }

    if (response.state?.error) {
      this.userService?.setUserStatus(response.state);
      this.appService?.setAccessAllowed(false);
      this.navigationService?.goToState(AppState.PanelAcl);
      return;
    }

    this.navigationService?.goToState(AppState.PanelDashboard);
  }

  private handleLoginError(error: unknown): void {
    const { data } = (error ?? {}) as LoginHttpError;
    const errorCode = data?.error ?? '';
    const messageKey = LOGIN_ERROR_MESSAGE_KEYS[errorCode] ?? MFA_ERROR_MESSAGE_KEYS[errorCode];

    this.showError(messageKey ?? GENERIC_ERROR_MESSAGE_KEY);
  }

  private showError(messageKey: string): void {
    this.toastService?.error(this.i18nService?.instant(messageKey) ?? messageKey);
  }

  private migrateLegacyLoginData(): string | null {
    const legacyBlob = localStorage.getItem(LEGACY_LOGIN_DATA_KEY);

    if (legacyBlob === null) {
      return localStorage.getItem(REMEMBERED_LOGIN_KEY);
    }

    localStorage.removeItem(LEGACY_LOGIN_DATA_KEY);

    const legacyLogin = extractLegacyLogin(legacyBlob);
    if (!legacyLogin) {
      return localStorage.getItem(REMEMBERED_LOGIN_KEY);
    }

    localStorage.setItem(REMEMBERED_LOGIN_KEY, legacyLogin);
    return legacyLogin;
  }

  private prefillRememberedLogin(rememberedLogin: string | null): void {
    if (rememberedLogin) {
      this.loginForm.patchValue({ userloginRaw: rememberedLogin, rememberPassword: true });
    }
  }

  private persistRememberedLogin(normalizedLogin: string): void {
    if (this.loginForm.getRawValue().rememberPassword) {
      localStorage.setItem(REMEMBERED_LOGIN_KEY, normalizedLogin);
      return;
    }
    localStorage.removeItem(REMEMBERED_LOGIN_KEY);
  }

  private sendDeviceToken(): void {
    try {
      const deviceToken = this.securityService?.getDeviceTokenCookie();
      if (deviceToken) {
        this.notificationService?.saveDeviceToken(deviceToken).catch(() => undefined);
      }
    } catch {
      return;
    }
  }

  private hideChatWidget(): void {
    const widget = document.getElementById('md-app-widget');
    if (widget) {
      widget.style.display = 'none';
    }
  }

  private reachableCountryUrl(code: string): string | null {
    const { hostname, port } = window.location;
    const url = resolveCountryUrl(hostname, code, port);

    return url && isSafeCountryUrl(url, hostname) ? url : null;
  }

  private buildCountryOptions(): SelectOption[] {
    return COUNTRY_OPTIONS.map((country) => ({
      value: country.code,
      label: this.i18nService?.instant(country.labelKey) ?? country.code,
      disabled: country.code !== this.currentCountry && !this.reachableCountryUrl(country.code),
    }));
  }

  private buildLabels(): LoginLabels {
    const t = (key: string): string => this.i18nService?.instant(key) ?? key;

    return {
      title: t('LOGIN.LOGIN_CARD_TITLE_LOGIN'),
      username: t('LOGIN.FORM.USERNAME.LABEL'),
      usernamePlaceholder: t('LOGIN.FORM.USERNAME.PLACEHOLDER'),
      usernameError: t('LOGIN.FORM.EMAIL.ERROR'),
      password: t('LOGIN.FORM.PASSWORD.LABEL'),
      passwordPlaceholder: t('LOGIN.FORM.PASSWORD.PLACEHOLDER'),
      passwordError: t('LOGIN.FORM.PASSWORD.ERROR'),
      requiredField: t('GENERIC.ERRORS.REQUIRED_FIELD'),
      rememberMe: t('LOGIN.FORM.REMEMBER_ME'),
      signIn: t('LOGIN.FORM.SIGNIN'),
      signingIn: t('LOGIN.FORM.SIGNING_IN'),
      showPassword: t('LOGIN.FORM.SHOW_PASSWORD'),
      hidePassword: t('LOGIN.FORM.HIDE_PASSWORD'),
      country: t('LOGIN.FORM.CHANGE_COUNTRY'),
    };
  }
}
